import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { RefreshCw, ShieldCheck, AlertTriangle } from "lucide-react";
import TaxDisclaimer from "@/components/tax/TaxDisclaimer";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * Compact strip showing the last time BuildIntel's ATO ruleset was cross-checked
 * against ato.gov.au, plus the FY26 rate snapshot.
 */
export default function AtoUpdatesCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    api
      .get("/tax/ato-updates")
      .then(({ data: res }) => {
        if (mounted) setData(res);
      })
      .catch((err) => devLog.warn("ato-updates load failed", err));
    return () => {
      mounted = false;
    };
  }, []);

  const overdue = data?.review_due;
  const c = overdue ? "#FBBF24" : "#34D399";

  return (
    <section
      className="bi-surface p-4 space-y-2"
      data-testid="ato-updates"
    >
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2" style={{ color: c }}>
          {overdue ? <AlertTriangle className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
          <span className="text-[10px] uppercase tracking-[0.2em] font-heading">
            ATO Ruleset Status
          </span>
        </div>
        <span
          className="ml-auto text-[10px] px-2 py-0.5 rounded-full font-heading uppercase tracking-wider"
          style={{ background: `${c}22`, color: c }}
        >
          {overdue ? "Review due" : "Up to date"}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="bi-surface-2 p-2 rounded-lg">
          <div className="text-[9px] uppercase tracking-wider text-white/50">Last reviewed</div>
          <div className="font-heading font-extrabold text-sm text-white">
            {data?.last_reviewed ? fmtDate(data.last_reviewed) : "…"}
          </div>
        </div>
        <div className="bi-surface-2 p-2 rounded-lg">
          <div className="text-[9px] uppercase tracking-wider text-white/50">Next review</div>
          <div className="font-heading font-extrabold text-sm" style={{ color: c }}>
            {data?.next_review_due ? fmtDate(data.next_review_due) : "…"}
          </div>
        </div>
      </div>

      {data?.rates && (
        <div className="grid grid-cols-3 gap-1.5 pt-1" data-testid="ato-rates">
          <div className="bi-surface-2 p-2 rounded-lg text-center">
            <div className="text-[9px] uppercase tracking-wider text-white/50">Vehicle</div>
            <div className="font-heading font-extrabold text-sm">
              {data.rates.vehicle_cents_per_km}c/km
            </div>
          </div>
          <div className="bi-surface-2 p-2 rounded-lg text-center">
            <div className="text-[9px] uppercase tracking-wider text-white/50">WFH</div>
            <div className="font-heading font-extrabold text-sm">
              {Math.round(data.rates.home_office_hourly * 100)}c/hr
            </div>
          </div>
          <div className="bi-surface-2 p-2 rounded-lg text-center">
            <div className="text-[9px] uppercase tracking-wider text-white/50">Write-off</div>
            <div className="font-heading font-extrabold text-sm">
              ${data.rates.instant_asset_writeoff.toLocaleString()}
            </div>
          </div>
        </div>
      )}

      <div className="text-[10px] text-white/40 inline-flex items-center gap-1 pt-1">
        <RefreshCw className="w-3 h-3" /> Manually curated · rates snapshot for FY26
      </div>

      <TaxDisclaimer compact />
    </section>
  );
}
